import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Button, Autocomplete, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Snackbar, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, FormGroup, FormControlLabel, Checkbox, CircularProgress
} from '@mui/material';
import axios from 'axios';
import * as XLSX from 'xlsx';
import CashReport from '../reports_doc/CashReport';
import BankReport from '../reports_doc/BankReport';

interface User {
  id: number;
  username: string;
  pharmacyNum: string;
}

interface TableField {
  id: number;
  cName: string;
  cType: string;
  displayName: string;
  InputBlock: number;
  formula: string;
  sortByGroup: string;
  important: number;
  statisticsHide: number;
  hideInStatTable: number;
}

interface TableData {
  [key: string]: string | number;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

interface StoredReportData {
  tableData: TableData[];
  tableFields: TableField[];
  username: string;
  startDate: string;
  endDate: string;
  timestamp: number;
}

const PeriodReport: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [employeeName, setEmployeeName] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [tableFields, setTableFields] = useState<TableField[]>([]);
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [isCustomExportOpen, setIsCustomExportOpen] = useState(false);
  const [selectedFields, setSelectedFields] = useState<string[]>(['date']);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    document.title = 'Просмотр отчета | Zerde';

    const storedDataStr = localStorage.getItem('periodReportData');
    if (storedDataStr) {
      const storedData: StoredReportData = JSON.parse(storedDataStr);
      const currentTime = Date.now();

      if (currentTime - storedData.timestamp < 600000) {
        setTableData(storedData.tableData);
        setTableFields(storedData.tableFields);
        setSelectedUser({
          id: 0,
          username: storedData.username,
          pharmacyNum: ''
        });
        setStartDate(storedData.startDate);
        setEndDate(storedData.endDate);

        setSnackbar({
          open: true,
          message: 'Данные загружены из предыдущего сеанса',
          severity: 'info',
        });
      }
    }
  }, []);

  const logAction = async (action: string) => {
    const currentRole = localStorage.getItem('role') || 'unknown';
    const currentUsername = localStorage.getItem('username') || 'unknown';
    try {
      await axios.post('https://tbl.zerdegroup.kz/api/log', {
        role: currentRole,
        username: currentUsername,
        action
      });
    } catch (error) {
      console.error('Ошибка записи действия:', error);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    fetchUsers();
    fetchTableStructure();
  }, []);

  useEffect(() => {
    if (selectedUser && startDate && endDate) {
      fetchData();
    }
  }, [selectedUser, startDate, endDate]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/users-with-pharmacy');
      if (response.data.success) {
        setUsers(response.data.users);
      } else {
        setSnackbar({
          open: true,
          message: 'Не удалось загрузить список пользователей',
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('Ошибка при загрузке пользователей:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка при загрузке пользователей',
        severity: 'error',
      });
    }
  };

  const fetchTableStructure = async () => {
    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/table-fields');
      let filteredFields = response.data.filter((field: TableField) => field.hideInStatTable === 0);

      if (!filteredFields.some((field: TableField) => field.cName === 'change_balance_end_of_day')) {
        filteredFields.push({
          cName: 'change_balance_end_of_day',
          displayName: 'Остаток мелочи на конец дня',
          statisticsHide: 0,
          hideInStatTable: 0
        });
      }

      setTableFields(filteredFields);
    } catch (error) {
      console.error('Ошибка при получении структуры таблицы:', error);
    }
  };

  const fetchData = async () => {
    if (!selectedUser || !startDate || !endDate) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/user-data', {
        params: {
          username: selectedUser.username,
          startDate,
          endDate
        }
      });

      const formattedData = response.data.map((row: TableData) => ({
        ...row,
        date: new Date(row.date).toLocaleDateString('ru-RU')
      }));

      setTableData(formattedData);

      const storageData: StoredReportData = {
        tableData: formattedData,
        tableFields,
        username: selectedUser.username,
        startDate,
        endDate,
        timestamp: Date.now()
      };
      localStorage.setItem('periodReportData', JSON.stringify(storageData));

      await logAction(`сформировал отчет для пользователя "${selectedUser.username}" за период ${startDate} - ${endDate} | Сформировать отчет`);
    } catch (error) {
      console.error('Ошибка при получении данных:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка при загрузке данных',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserChange = (event: React.SyntheticEvent, value: User | null) => {
    setSelectedUser(value);
    setTableData([]);
    if (value) {
      fetchEmployeeData(value.username);
    } else {
      setEmployeeName(null);
    }
  };

  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(event.target.value);
    setTableData([]);
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value);
    setTableData([]);
  };

  const formatNumber = (num: number | string | undefined): string => {
    if (num === undefined || num === null) {
      return '-';
    }
    if (typeof num === 'string') {
      num = parseFloat(num);
    }
    if (isNaN(num)) {
      return '-';
    }
    return num.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
  };

  const calculateTotals = () => {
    const totals: TableData = {};

    tableFields.forEach(field => {
      if (field.statisticsHide === 0 && field.cName !== 'date') {
        totals[field.cName] = tableData.reduce((sum, row) => {
          const value = Number(row[field.cName]) || 0;
          return sum + value;
        }, 0);
      }
    });

    return totals;
  };

  const exportToExcel = () => {
    const exportData = tableData.map(row => {
      const newRow: any = {};
      tableFields.forEach(field => {
        if (field.hideInStatTable === 0) {
          newRow[field.displayName] = row[field.cName];
        }
      });
      return newRow;
    });

    const totals = calculateTotals();
    const totalsRow: any = { 'Дата': 'Итого:' };
    tableFields.forEach(field => {
      if (field.hideInStatTable === 0 && field.cName !== 'date') {
        totalsRow[field.displayName] = totals[field.cName];
      }
    });
    exportData.push(totalsRow);

    const worksheet = XLSX.utils.json_to_sheet(exportData);

    const columnWidths = tableFields
      .filter(field => field.hideInStatTable === 0)
      .map(field => ({ wch: Math.max(field.displayName.length, 15) }));
    worksheet['!cols'] = columnWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "UserData");

    XLSX.writeFile(workbook, `UserData_${selectedUser?.username}_${startDate}_${endDate}.xlsx`);
    logAction(`сформировал excel отчет для пользователя "${selectedUser?.username}" за период ${startDate} - ${endDate} | Сформировать отчет`);
  };

  const handleCustomExportOpen = () => {
    setSelectedFields(['date']);
    setIsCustomExportOpen(true);
  };

  const handleCustomExportClose = () => {
    setSelectedFields(['date']);
    setIsCustomExportOpen(false);
  };

  const handleFieldToggle = (fieldName: string) => {
    setSelectedFields(prev => {
      if (fieldName === 'date') return prev;
      return prev.includes(fieldName)
        ? prev.filter(f => f !== fieldName)
        : [...prev, fieldName];
    });
  };

  const handleCustomExport = () => {
    const exportData = tableData.map(row => {
      const newRow: any = {};
      selectedFields.forEach(fieldName => {
        const field = tableFields.find(f => f.cName === fieldName);
        if (field) {
          newRow[field.displayName] = row[fieldName];
        }
      });
      return newRow;
    });

    const totals = calculateTotals();
    const totalsRow: any = { 'Дата': 'Итого:' };
    selectedFields.forEach(fieldName => {
      if (fieldName !== 'date') {
        const field = tableFields.find(f => f.cName === fieldName);
        if (field) {
          totalsRow[field.displayName] = totals[fieldName];
        }
      }
    });
    exportData.push(totalsRow);

    const worksheet = XLSX.utils.json_to_sheet(exportData);

    const columnWidths = selectedFields
      .map(fieldName => {
        const field = tableFields.find(f => f.cName === fieldName);
        return { wch: Math.max(field?.displayName.length || 0, 15) };
      });
    worksheet['!cols'] = columnWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "CustomReport");

    const currentDate = new Date().toISOString().split('T')[0];
    XLSX.writeFile(workbook, `CustomReport_${currentDate}.xlsx`);

    handleCustomExportClose();
    logAction(`сформировал кастомный excel отчет для пользователя "${selectedUser?.username}" за период ${startDate} - ${endDate} | Сформировать отчет`);
  };

  const fetchEmployeeData = async (username: string) => {
    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/users-data-mathrep', {
        params: { login: username }
      });
      setEmployeeName(response.data.fullName);
    } catch (error) {
      console.error('Ошибка при получении данных сотрудника:', error);
      setEmployeeName(null);
    }
  };

  return (
    <Box sx={{ maxWidth: '100%', overflowX: 'auto' }}>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mt: 1, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: '20px', alignItems: 'flex-end' }}>
          <Autocomplete
            size='small'
            options={users}
            getOptionLabel={(option) => `${option.username} (Аптека: ${option.pharmacyNum})`}
            renderInput={(params) => <TextField {...params} label="Выберите пользователя" />}
            onChange={handleUserChange}
            value={selectedUser}
            sx={{
              width: '300px',
            }}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              size='small'
              label="Начальная дата"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={handleStartDateChange}
            />
            <TextField
              size='small'
              label="Конечная дата"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={handleEndDateChange}
            />
          </Box>
        </Box>
        <Box>
          <Button
            variant="contained"
            onClick={exportToExcel}
            disabled={tableData.length === 0}
            color='warning'
            sx={{ mr: 1 }}
          >
            EXCEL
          </Button>
          <Button
            variant="contained"
            color='warning'
            onClick={handleCustomExportOpen}
            disabled={tableData.length === 0}
            sx={{ mr: 1 }}
          >
            custom
          </Button>
          <CashReport
            tableData={tableData}
            selectedUser={selectedUser}
            startDate={startDate}
            endDate={endDate}
            employeeName={employeeName}
          />
          <BankReport
            tableData={tableData}
            selectedUser={selectedUser}
            startDate={startDate}
            endDate={endDate}
            employeeName={employeeName}
          />
          {/* <MathReport
            tableData={tableData}
            selectedUser={selectedUser}
            startDate={startDate}
            endDate={endDate}
          /> */}
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        tableData.length > 0 && (
          <TableContainer component={Paper}
            sx={{
              maxHeight: '650px'
            }}>
            <Table sx={{ minWidth: 400 }} size="small">
              <TableHead>
                <TableRow>
                  {tableFields.map((field) => (
                    <TableCell
                      key={field.cName}
                      sx={{
                        whiteSpace: 'nowrap',
                        backgroundColor: '#1976d2',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        color: 'white',
                      }}
                    >
                      {field.displayName}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{ '&:nth-of-type(odd)': { backgroundColor: '#ffffff' } }}
                  >
                    {tableFields.map((field) => (
                      <TableCell
                        key={field.cName}
                        sx={{
                          whiteSpace: 'nowrap',
                          ...(field.cName === 'date' ? { position: 'sticky', left: 0, backgroundColor: '#1976d2', zIndex: 1, color: 'white' } : {})
                        }}
                      >
                        {field.cName === 'date' ? row[field.cName] : formatNumber(row[field.cName])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                <TableRow sx={{ fontWeight: 'bold', backgroundColor: '#1976d2' }}>
                  <TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, color: 'white', backgroundColor: '#1976d2' }}>Итого:</TableCell>
                  {tableFields.slice(1).map((field) => (
                    <TableCell key={field.cName} sx={{ color: 'white' }}>
                      {calculateTotals()[field.cName] !== undefined
                        ? formatNumber(calculateTotals()[field.cName])
                        : ''}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={isCustomExportOpen}
        onClose={handleCustomExportClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Выберите поля для экспорта</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormGroup>
              {tableFields.map((field) => (
                <FormControlLabel
                  key={field.cName}
                  control={
                    <Checkbox
                      checked={selectedFields.includes(field.cName)}
                      onChange={() => handleFieldToggle(field.cName)}
                      disabled={field.cName === 'date'}
                    />
                  }
                  label={field.displayName}
                />
              ))}
            </FormGroup>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCustomExportClose} color="primary">
            Отмена
          </Button>
          <Button
            onClick={handleCustomExport}
            color="primary"
            variant="contained"
            disabled={selectedFields.length <= 1}
          >
            Сформировать
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PeriodReport;